// src/utils/webvtt-cue.ts
var PlainTextVTTCue = class {
  startTime;
  endTime;
  text;
  id;
  vertical = "";
  snapToLines = true;
  line = "auto";
  lineAlign = "start";
  position = "auto";
  positionAlign = "auto";
  size = 100;
  align = "center";
  constructor(startTime, endTime, text) {
    this.startTime = startTime;
    this.endTime = endTime;
    this.text = text;
  }
  /**
   * Simplified implementation. Not according to spec. Just strips the tags and
   * expects the rest to be plain text (HTML entities not supported).
   */
  getCueAsHTML() {
    const doc = document.createDocumentFragment();
    doc.textContent = this.text.replace(/<[/\w][^<]*>/g, "");
    return doc;
  }
};
var WebVTTCueImpl = typeof VTTCue !== "undefined" ? VTTCue : typeof TextTrackCue !== "undefined" ? TextTrackCue : PlainTextVTTCue;
function createWebVTTCue(data) {
  const cue = new WebVTTCueImpl(data.startTime, data.endTime, data.text);
  cue.id = data.id;
  cue.vertical = data.vertical;
  cue.snapToLines = data.snapToLines;
  cue.line = data.line;
  cue.lineAlign = data.lineAlign;
  cue.position = data.position;
  cue.positionAlign = data.positionAlign;
  cue.size = data.size;
  cue.align = data.align;
  return cue;
}

// src/utils/webvtt-parser.ts
function parseWebVTT(data) {
  const lines = data.replace(/\0/g, "\uFFFD").replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  if (lines.length === 0 || !/^WEBVTT\b/.exec(lines[0])) {
    throw new Error("Not WebVTT data.");
  }
  const cues = [];
  let i = 0;
  while (++i < lines.length) {
    if (!/[\d:.]+\s+-->\s+[\d:.]+/.exec(lines[i])) {
      continue;
    }
    const id = lines[i - 1] !== "" ? lines[i - 1] : void 0;
    const cueConfig = lines[i].split(/\s+/);
    const startTime = parseWebVTTCueTimestamp(cueConfig[0]);
    const endTime = parseWebVTTCueTimestamp(cueConfig[2]);
    const settings = parseWebVTTCueSettings(cueConfig.slice(3));
    const payload = [];
    while (++i < lines.length && lines[i] !== "") {
      payload.push(lines[i]);
    }
    cues.push(
      createWebVTTCue({
        id,
        startTime,
        endTime,
        text: payload.join("\n"),
        ...settings
      })
    );
  }
  return { cues };
}
function parseWebVTTCueTimestamp(timestamp) {
  const values = timestamp.split(":");
  return values.map((value) => Number.parseFloat(value)).reduce((totalSeconds, value, index) => totalSeconds + value * 60 ** (values.length - 1 - index), 0);
}
function parseWebVTTCueSettings(rawSettings) {
  const rawSettingsArray = Array.isArray(rawSettings) ? rawSettings : rawSettings.split(/\s+/);
  const settings = {
    vertical: "",
    snapToLines: true,
    line: "auto",
    lineAlign: "start",
    position: "auto",
    positionAlign: "auto",
    align: "center",
    size: 100
  };
  for (const rawSetting of rawSettingsArray) {
    const colonIndex = rawSetting.indexOf(":");
    const name = colonIndex > 0 ? rawSetting.substr(0, colonIndex) : rawSetting;
    const value = colonIndex > 0 ? rawSetting.substr(colonIndex + 1) : "";
    if (name === "vertical") {
      if (value === "lr" || value === "rl") {
        settings.vertical = value;
      }
    } else if (name === "line") {
      const [offset, lineAlign] = value.split(",");
      let parsedOffset;
      if (offset.charAt(offset.length - 1) === "%") {
        settings.snapToLines = false;
        parsedOffset = Number.parseFloat(offset);
      } else {
        parsedOffset = Number.parseInt(offset, 10);
      }
      if (!Number.isNaN(parsedOffset)) {
        settings.line = parsedOffset;
      }
      if (lineAlign !== void 0 && ["start", "center", "end"].includes(lineAlign)) {
        settings.lineAlign = lineAlign;
      }
    } else if (name === "position") {
      const [position, positionAlign] = value.split(",");
      const parsedPosition = Number.parseFloat(position);
      if (!Number.isNaN(parsedPosition)) {
        settings.position = parsedPosition;
      }
      if (positionAlign !== void 0 && ["line-left", "center", "line-right"].includes(positionAlign)) {
        settings.positionAlign = positionAlign;
      }
    } else if (name === "size") {
      const parsedSize = Number.parseFloat(value);
      if (!Number.isNaN(parsedSize)) {
        settings.size = parsedSize;
      }
    } else if (name === "align") {
      if (["start", "center", "end", "left", "right"].includes(value)) {
        settings.align = value;
      }
    }
  }
  return settings;
}

// src/utils/exposed-promise.ts
var ExposedPromise = class {
  __resolved = false;
  __resolvedValue;
  __resolve;
  __rejected = false;
  __rejectedReason;
  __reject;
  then;
  catch;
  finally;
  [Symbol.toStringTag] = "ExposedPromise";
  constructor() {
    const promise = new Promise((resolve, reject) => {
      this.__resolve = resolve;
      this.__reject = reject;
      if (this.__resolved) {
        resolve(this.__resolvedValue);
      }
      if (this.__rejected) {
        reject(this.__rejectedReason);
      }
    });
    this.then = promise.then.bind(promise);
    this.catch = promise.catch.bind(promise);
    if ("finally" in promise) {
      this.finally = promise.finally.bind(promise);
    } else {
      this.finally = () => {
        throw new Error("Finally not supported.");
      };
    }
  }
  resolve(value) {
    this.__resolved = !this.__rejected;
    this.__resolvedValue = value;
    this.__resolve?.(value);
  }
  reject(reason) {
    this.__rejected = !this.__resolved;
    this.__rejectedReason = reason;
    this.__reject?.(reason);
  }
};

export {
  parseWebVTT,
  ExposedPromise
};
